import api from "./baseApi";
import endpoints from "./endpoints";

/**
 * Get venues with pagination + optional filters
 */
export const getVenues = async (params = {}) => {
  try {
    const res = await api.get(endpoints.venue.base, {
      params: {
        page: params.page || 1,
        perPage: params.perPage || 10,

        // Sorting
        sortField: params.sortField || undefined,
        sortOrder: params.sortOrder || undefined,

        // Filters
        ...(params.filters || {}),
      },
    });

    return res.data;
  } catch (err) {
    const message =
      err.response?.data?.message || "failed to fetch venues";
    throw new Error(message);
  }
};

/**
 * Get single venue by ID
 */
export const getVenueById = async (id) => {
  try {
    const res = await api.get(endpoints.venue.detail(id));
    return res.data;
  } catch (err) {
    const message =
      err.response?.data?.message || "failed to fetch venue";
    throw new Error(message);
  }
};

/**
 * Create a new venue
 */
export const createVenue = async (payload) => {
  try {
    const formData = new FormData();

    formData.append("name", payload.name);
    formData.append("description", payload.description || "");
    formData.append("remark", payload.remark || "");

    formData.append("requiresPayment", payload.requiresPayment ? 1 : 0);
    formData.append("requiresApproval", payload.requiresApproval ? 1 : 0);

    formData.append(
      "allowPublicBooking",
      payload.allowPublicBooking ? 1 : 0
    );

    // Payment fields
    if (payload.requiresPayment) {
      formData.append("cost", payload.cost ? String(payload.cost) : "");
      formData.append("peakCost", payload.peakCost ? String(payload.peakCost) : "");
      formData.append("chargedBy", payload.chargedBy || "");
    }

    // ✅ Lead time fields
    if (payload.minLeadTime) {
      formData.append("minLeadTime", JSON.stringify(payload.minLeadTime));
    }

    if (payload.maxLeadTime) {
      formData.append("maxLeadTime", JSON.stringify(payload.maxLeadTime));
    }

    // Operating hours
    if (payload.operatingHours) {
      formData.append(
        "operatingHours",
        JSON.stringify(payload.operatingHours)
      );
    }

    // Devices
    if (payload.deviceIds && Array.isArray(payload.deviceIds)) {
      formData.append("deviceIds", JSON.stringify(payload.deviceIds));
    }

    // Photo
    if (payload.photo) {
      formData.append("photo", payload.photo);
    }

    const res = await api.post(endpoints.venue.base, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return res.data;
  } catch (err) {
    const message =
      err.response?.data?.message || "failed to create venue";
    throw new Error(message);
  }
};

/**
 * Update an existing venue
 */
export const updateVenue = async (id, payload) => {
  try {
    const formData = new FormData();

    formData.append("name", payload.name);
    formData.append("description", payload.description || "");
    formData.append("remark", payload.remark || "");

    formData.append("requiresPayment", payload.requiresPayment ? 1 : 0);
    formData.append("requiresApproval", payload.requiresApproval ? 1 : 0);

    if (payload.allowPublicBooking !== undefined) {
      formData.append(
        "allowPublicBooking",
        payload.allowPublicBooking ? 1 : 0
      );
    }

    // Payment fields
    if (payload.requiresPayment) {
      formData.append("cost", payload.cost ? String(payload.cost) : "");
      formData.append("chargedBy", payload.chargedBy || "");
    } else {
      formData.append("cost", "");
      formData.append("chargedBy", "");
    }

    // ✅ Lead time fields
    if (payload.minLeadTime !== undefined) {
      formData.append("minLeadTime", JSON.stringify(payload.minLeadTime));
    }

    if (payload.maxLeadTime !== undefined) {
      formData.append("maxLeadTime", JSON.stringify(payload.maxLeadTime));
    }

    // Operating hours
    if (payload.operatingHours) {
      formData.append(
        "operatingHours",
        JSON.stringify(payload.operatingHours)
      );
    }

    // Devices
    if (payload.deviceIds && Array.isArray(payload.deviceIds)) {
      formData.append("deviceIds", JSON.stringify(payload.deviceIds));
    }

    // Photo
    if (payload.photo) {
      formData.append("photo", payload.photo);
    }

    const res = await api.put(endpoints.venue.detail(id), formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return res.data;
  } catch (err) {
    const message =
      err.response?.data?.message || "failed to update venue";
    throw new Error(message);
  }
};

/**
 * Delete a venue
 */
export const deleteVenue = async (id) => {
  try {
    const res = await api.delete(endpoints.venue.detail(id));
    return res.data;
  } catch (err) {
    const message =
      err.response?.data?.message || "failed to delete venue";
    throw new Error(message);
  }
};